import { translate } from 'support/translate';

export const MILLISECONDS_IN_SECOND = 1000;
export const MILLISECONDS_IN_MINUTE = 60 * MILLISECONDS_IN_SECOND;
export const MILLISECONDS_IN_HOUR = 60 * MILLISECONDS_IN_MINUTE;
export const MILLISECONDS_IN_DAY = 24 * MILLISECONDS_IN_HOUR;

const diffHelper = (later: Date, earlier: Date, milliseconds: number) => {
  return (later.getTime() - earlier.getTime()) / milliseconds;
};

export const secondsToMilliseconds = (seconds: number) => {
  return MILLISECONDS_IN_SECOND * seconds;
};

export const minutesToMilliseconds = (minutes: number) => {
  return MILLISECONDS_IN_MINUTE * minutes;
};

export const millisecondsToMinutes = (milliseconds: number) => {
  return milliseconds / MILLISECONDS_IN_MINUTE;
};

export const diffInMinutes = (later: Date, earlier: Date) => {
  return diffHelper(later, earlier, MILLISECONDS_IN_HOUR);
};

export const hoursToMilliseconds = (hours: number) => {
  return MILLISECONDS_IN_HOUR * hours;
};

export const millisecondsToHours = (milliseconds: number) => {
  return milliseconds / MILLISECONDS_IN_HOUR;
};

export const diffInHours = (later: Date, earlier: Date) => {
  return diffHelper(later, earlier, MILLISECONDS_IN_HOUR);
};

export const daysToMilliseconds = (days: number) => {
  return MILLISECONDS_IN_DAY * days;
};

export const diffInDays = (later: Date, earlier: Date) => {
  return diffHelper(later, earlier, MILLISECONDS_IN_DAY);
};

export const getClosestMinute = (date: Date) => {
  date.setMinutes(date.getMinutes(), 0, 0);
  return date;
};

export const getClosestThirtyMinuteOffsetDate = (date: Date) => {
  const currentMinutes = date.getMinutes();
  const thirtyMinuteOffset = currentMinutes % 30;

  date.setMinutes(currentMinutes - thirtyMinuteOffset, 0, 0);
  return date;
};

export const timeTo12HourClockHoursMinutes = (time: Date, full = false) => {
  const hours = time.getHours() % 12 === 0 ? 12 : time.getHours() % 12;

  let minutes = '00';
  if (full || time.getMinutes() > 1) {
    const minutesZeroPadding = time.getMinutes() < 10 ? '0' : '';
    minutes = `${minutesZeroPadding}${time.getMinutes()}`;
  }

  const pmOrAm =
    time.getHours() >= 12
      ? translate('global.lowercasePM')
      : translate('global.lowercaseAM');

  return `${hours}:${minutes}${pmOrAm}`;
};

export const secondsToContentDuration = (seconds: number) => {
  const milliseconds = secondsToMilliseconds(seconds);
  const hours = Math.floor(millisecondsToHours(milliseconds));
  const minutes = Math.ceil(millisecondsToMinutes(milliseconds)) % 60;

  return hours === 0
    ? translate('global.durationMinutes', minutes)
    : minutes === 0
    ? translate('global.durationHours', hours)
    : translate('global.durationHoursMinutes', hours, minutes);
};

export const millisecondsToContentDuration = (milliseconds: number) => {
  const hours = Math.floor(millisecondsToHours(milliseconds));
  const minutes = Math.ceil(millisecondsToMinutes(milliseconds));

  return hours === 0
    ? translate('global.durationMinutesNoSpace', minutes)
    : translate('global.durationHoursMinutesNoSpace', hours, minutes % 60);
};

/**
@param totalSeconds - Total time in seconds
@returns A string representing the time in HH:MM:SS format
*/
export const convertSecondsToTimeString = (
  totalSeconds: number | string | undefined,
) => {
  if (!totalSeconds) return '00:00:00';
  if (typeof totalSeconds === 'string') {
    totalSeconds = parseInt(totalSeconds, 10);
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  const addPadding = (time: number) => time.toString().padStart(2, '0');

  return `${addPadding(hours)}:${addPadding(minutes)}:${addPadding(seconds)}`;
};

/**
@param timeString - A string representing the time in HH:MM:SS format
@returns Total time in seconds
*/
export const convertTimeStringToSeconds = (timeString: string) => {
  const [hours = 0, minutes = 0, seconds = 0] = timeString
    .split(':')
    .map(Number) as [number, number, number];
  return hours * 3600 + minutes * 60 + seconds;
};
/**
 *
 * @param date - date in YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS format
 * @returns date in MM/DD/YYYY format
 */
export const convertToSlashDateFormat = (date: string | undefined) => {
  if (!date) return '';
  const dateOnly = date.split('T')[0] ?? date;
  const [year, month, day] = dateOnly.split('-');
  return `${month}/${day}/${year}`;
};
